// src/utils/announcementTicker.js
//
// Runtime announcement ticker timing helper
// ========================================
//
// PURPOSE
// -------
// Calculates the CSS animation duration for sliding runtime announcement text.
//
// The hb_visits announcement model stores ticker speed as a semantic business
// value, not as raw CSS seconds:
// - very_slow
// - slow
// - normal
// - slightly_fast
// - fast
// - very_fast
// - maximum
//
// Why not store seconds directly?
// -------------------------------
// A raw value such as 60 seconds is hard to understand for an admin user. It is
// also not enough on its own because the same 60 seconds may be too fast for a
// long message and unnecessarily slow for a short message.
//
// This helper combines two inputs:
// 1. the resolved announcement text length,
// 2. the semantic ticker_speed selected in Announcement Management.
//
// This keeps runtime configuration business-friendly while still making the
// actual banner readable on public and internal shells.

export const ANNOUNCEMENT_TICKER_SPEEDS = [
  'very_slow',
  'slow',
  'normal',
  'slightly_fast',
  'fast',
  'very_fast',
  'maximum',
];

const SPEED_MULTIPLIER = {
  very_slow: 1.75,
  slow: 1.35,
  normal: 1,
  slightly_fast: 0.85,
  fast: 0.70,
  very_fast: 0.55,
  maximum: 0.40,
};

function normalizeTickerSpeed(value) {
  const normalized = String(value || 'normal').trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(SPEED_MULTIPLIER, normalized)
    ? normalized
    : 'normal';
}

function clamp(number, min, max) {
  return Math.min(max, Math.max(min, number));
}

/**
 * Returns a CSS duration string, for example "72s".
 *
 * The formula intentionally starts with a conservative base duration. Public
 * shop announcements are often operational messages that visitors must be able
 * to read without chasing the text. The selected ticker_speed then modifies the
 * duration with a multiplier:
 * - larger duration  -> slower visual movement,
 * - smaller duration -> faster visual movement.
 */
export function getAnnouncementTickerDuration(announcement) {
  const message = String(announcement?.message || '').trim();
  const linkLabel = String(announcement?.link_label || '').trim();
  const totalLength = message.length + linkLabel.length;

  // Base duration derived from text length. The limits protect against both
  // very short and accidentally very long input.
  const baseSeconds = clamp(Math.round(28 + totalLength * 0.65), 50, 120);

  const tickerSpeed = normalizeTickerSpeed(announcement?.ticker_speed);
  const seconds = Math.round(baseSeconds * SPEED_MULTIPLIER[tickerSpeed]);

  // Final clamp after the speed multiplier. This keeps "very_slow" usable for
  // long messages without creating multi-minute animations, while still allowing
  // deliberately short high-speed modes for short operational banners.
  //
  // The lower bound is intentionally lower than in the first ticker_speed batch
  // because "very_fast" and "maximum" are explicit admin choices. They should
  // be used mainly for short messages where quick repetition matters more than
  // long reading time.
  return `${clamp(seconds, 18, 180)}s`;
}

export function getNormalizedAnnouncementTickerSpeed(value) {
  return normalizeTickerSpeed(value);
}
